import './App.css';
import './style/style.css';
import './style/notification.css';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Notification from './component/Notification';
import html_parse from 'html-react-parser';
import Info from './component/Info';
import DOMPurify from 'dompurify';
import Timetable from './component/Timetable';
import ToggleSwitch from './component/ToggleSwitch';
import RightsReserved from './component/RightsReserved';
import ContactSection from './component/ContactSection';
import UpdateNotification from './component/UpdateNotification';
import { AdProvider, useAds, AdCard, AuthProvider, ProtectedRoute } from './component/ads';
import AdWrapper from './component/ads/AdWrapper';
import { APP_INFO, logger } from './config/environment';
import { SHEET_CONFIG_URL } from './services/timetable';

// Lazy-loaded components
const AdAdmin = lazy(() => import('./component/ads/AdAdmin'));
const AdDashboard = lazy(() => import('./component/ads/AdDashboard'));
const DownloadPage = lazy(() => import('./component/DownloadPage'));
const Teachers = lazy(() => import('./component/Teachers'));
const Classrooms = lazy(() => import('./component/Classrooms'));

// const apk = '/Fast-Timetable.apk'

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <div className="loader"></div>
  </div>
);

const SectionAdCard = () => {
  const { getAdForPosition } = useAds();
  const ad = getAdForPosition('card');
  return ad ? <AdCard ad={ad} size="medium" position="after-content" /> : null;
};

function App() {
  const [loading, setLoading] = useState(false);
  // const [showDownload, setShowDownload] = useState(true);
  const [notification, setNotification] = useState(null); // { message, color } instead of a React element
  const versionCode = APP_INFO.VERSION_CODE;
  // const [showUpdate, setShowUpdate] = useState(false);
  // const [apkLink, setApkLink] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPwaInstall, setShowPwaInstall] = useState(false);

  const [activeTab, setActiveTab] = useState('timetable'); // 'timetable', 'teachers', 'classrooms'
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [showStickyAd, setShowStickyAd] = useState(() => {
    const dismissed = localStorage.getItem('stickyAdDismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 60 * 60 * 1000) return false;
    return true;
  });
  const [showTopBanner, setShowTopBanner] = useState(() => {
    const dismissed = localStorage.getItem('topBannerDismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 60 * 60 * 1000) return false;
    return true;
  });
  const tabSwitchCount = useRef(0);

  const [adsComponent, setAdsComponent] = useState(null);

  const showNotification = useCallback((message, color) => {
    setNotification({ message, color });
  }, []);

  const handleTabSwitch = useCallback((newTab) => {
    setActiveTab((prev) => {
      if (newTab !== prev) {
        tabSwitchCount.current++;
        if (tabSwitchCount.current % 3 === 0) {
          setShowInterstitial(true);
        }
      }
      return newTab;
    });
  }, []);


  useEffect(() => {
    let cancelled = false;
    const fetchSheetData = async () => {
      try {
        const response = await fetch(SHEET_CONFIG_URL);
        const json = await response.json();
        if (cancelled) return;
        if (json.versionCode > versionCode) {
          // setShowUpdate(true);
          localStorage.setItem('vcode', 'true');
        } else {
          localStorage.setItem('vcode', 'false');
        }
        // setApkLink(json.apklink);
        const sanitizedHtml = json.component ? DOMPurify.sanitize(json.component) : null;
        setAdsComponent(sanitizedHtml ? html_parse(sanitizedHtml) : null);
        localStorage.setItem('apk', json.apklink);
      } catch (err) {
        logger.error('Failed to fetch sheet data:', err);
        // setShowUpdate('true' === localStorage.getItem('vcode'));
      }
    };
    fetchSheetData();
    return () => { cancelled = true; };
    // Run once on mount only - the old deps caused an infinite refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const handleDownloadDismiss = useCallback((val) => setShowDownload(val), []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowPwaInstall(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setShowPwaInstall(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handlePwaInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowPwaInstall(false);
  };

  return (
    <AuthProvider>
    <AdProvider>
    <BrowserRouter>
      <Routes>
        <Route path='/' element={
          <div className="App">
          {/* <a href="#main-content" className="skip-link">Skip to main content</a> */}
          {/* Ads from Centralized AdProvider */}
          <AdWrapper 
            showTopBanner={showTopBanner}
            setShowTopBanner={setShowTopBanner}
            showStickyAd={showStickyAd}
            setShowStickyAd={setShowStickyAd}
            showInterstitial={showInterstitial}
            setShowInterstitial={setShowInterstitial}
            showSectionAd={false}
          />
          
          <Info/>
          <ToggleSwitch activeTab={activeTab} setActiveTab={handleTabSwitch} />
           {activeTab === 'timetable' && <Timetable loading={loading} setLoading={setLoading} showNotification={showNotification}/>}
           {activeTab === 'teachers' && <Suspense fallback={<LoadingFallback />}><Teachers /></Suspense>}
           {activeTab === 'classrooms' && <Suspense fallback={<LoadingFallback />}><Classrooms /></Suspense>}
           

           <SectionAdCard />
           
           {/* <a href={apk} download='FAST Timetable.apk'>
           {showDownload&&(<div className="download-apk-text" onTouchStart={()=>{handleDownloadDismiss(true)}} onMouseEnter={()=>{handleDownloadDismiss(true)}} onMouseLeave={()=>{handleDownloadDismiss(false)}}>Download APK </div>)}
           <div className="download-apk" style={{borderRadius: '50%', width: '50px', height: '50px'}} onTouchStart={()=>{handleDownloadDismiss(true)}} onMouseEnter={()=>{handleDownloadDismiss(true)}} onMouseLeave={()=>{handleDownloadDismiss(false)}}><i className="fa fa-arrow-down" style={{paddingLeft: '0px', color: 'white'}}></i></div>
           </a> */}

           {showPwaInstall && (
             <button
               type="button"
               onClick={handlePwaInstall}
               aria-label="Install app"
               title="Install app"
               className="download-apk"
               style={{ borderRadius: '50%', width: '50px', height: '50px', border: 'none', backgroundColor: '#5349be', cursor: 'pointer' }}>
               <i className="fa fa-arrow-down" aria-hidden="true" style={{color: 'white', paddingLeft: '0px', fontSize: '18px'}}/>
             </button>
           )}

           {notification && (
             <Notification message={notification.message} background={notification.color} setNotification={() => setNotification(null)}/>
           )}
           <ContactSection />
           {adsComponent}
           
           <UpdateNotification />
      
           {/* {showUpdate && <CheckUpdate apkLink={apkLink}/>} */}
           <RightsReserved/>
         </div>
        }/>
        <Route path='/admin/ads' element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdAdmin/></Suspense></ProtectedRoute>}/>
        <Route path='/admin/dashboard' element={<ProtectedRoute><Suspense fallback={<LoadingFallback />}><AdDashboard/></Suspense></ProtectedRoute>}/>
        <Route path='/download' element={<Suspense fallback={<LoadingFallback />}><DownloadPage/></Suspense>}/>
      </Routes>
    </BrowserRouter>
    </AdProvider>
    </AuthProvider>
  );
}

export default App;
